// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; captions label the adjacent select (same convention as the other admin screens).
//
// =============================================================================
// ProductMappingAdmin — Manage → Unleashed → Product Mapping
// =============================================================================
// Admin/SuperUser screen that assigns each fixed-enum orderable item kind
// (device / fee / shoe) an Unleashed product code from the catalogue. Rows come
// from REQUIRED_PRODUCT_MAP_KEYS (derived from the enums, so the screen always
// shows every key that must be mapped); the current code comes from
// `unleashed_product_map` (db-66). Any row still on the FIX_IT placeholder — or
// unmapped — is flagged so it can't be quietly missed. Uniforms are mapped on the
// supplier record, not here. Reference: docs/unleashed-interface.md.
// =============================================================================
import React from "react";
import { listProductMap, setMapping } from "../../data/unleashedProductMap";
import { listProducts } from "../../data/unleashedProducts";
import { FIX_IT_CODE, REQUIRED_PRODUCT_MAP_KEYS } from "../../services/orderItemKinds";
import { logAudit } from "../../utils/auditLog";

const KIND_LABEL = { device: "Devices", fee: "Fees", shoe: "Shoes" };
const KIND_ORDER = ["device", "fee", "shoe"];

// The QLD Stationery pack maps to a YEAR-SPECIFIC product code (db-115/116). The
// engine's yearBucket(child.yearLevel) picks the bucket; each maps to an STA code.
// Labels here are display-only — the bucket key is what's stored in year_bucket.
const STATIONERY_BUCKETS = [
  { bucket: "PREP", label: "Prep / Kindy" },
  { bucket: "Y1", label: "Year 1" },
  { bucket: "Y2", label: "Year 2" },
  { bucket: "Y3_4", label: "Years 3-4" },
  { bucket: "Y5_6", label: "Years 5-6" },
  { bucket: "HS", label: "Years 7-12 (High School)" },
];

export function ProductMappingAdmin() {
  const [mapRows, setMapRows] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [refreshTick, setRefreshTick] = React.useState(0);

  React.useEffect(
    function () {
      let cancelled = false;
      setLoading(true);
      Promise.all([listProductMap(), listProducts()]).then(function (res) {
        if (cancelled) return;
        setMapRows(res[0] || []);
        setProducts(res[1] || []);
        setLoading(false);
      });
      return function () {
        cancelled = true;
      };
    },
    [refreshTick]
  );

  function refresh() {
    setRefreshTick(function (n) {
      return n + 1;
    });
  }

  function showSaved(msg) {
    setSavedMsg(msg || "Saved ✓");
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  // Current product_code for a key's BASE row (year_bucket ""), or "" if none. Year-
  // specific stationery rows are skipped here — they have their own section below —
  // so the Fees "Stationery" row shows its base/fallback code, not a year variant.
  const byKey = React.useMemo(
    function () {
      const m = {};
      for (const r of mapRows) {
        if (r.year_bucket) continue;
        m[`${r.item_kind}|${r.item_key}`] = r.product_code;
      }
      return m;
    },
    [mapRows]
  );

  // Current STA code per stationery year bucket (fee / Stationery / <bucket>).
  const stationeryByBucket = React.useMemo(
    function () {
      const m = {};
      for (const r of mapRows) {
        if (r.item_kind === "fee" && r.item_key === "Stationery" && r.year_bucket) {
          m[r.year_bucket] = r.product_code;
        }
      }
      return m;
    },
    [mapRows]
  );

  // Catalogue options for the dropdowns, grouped by product_group (active only).
  const productGroups = React.useMemo(
    function () {
      const active = products.filter(function (p) {
        return !p.deactivated;
      });
      const groups = new Map();
      for (const p of active) {
        const g = p.product_group || "Other";
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g).push(p);
      }
      return [...groups.keys()]
        .sort(function (a, b) {
          return a.localeCompare(b);
        })
        .map(function (g) {
          return {
            group: g,
            items: groups.get(g).sort(function (a, b) {
              return a.product_code.localeCompare(b.product_code);
            }),
          };
        });
    },
    [products]
  );

  async function onPick(kind, key, productCode, yearBucketKey = "") {
    if (!productCode) return;
    const { error } = await setMapping(kind, key, productCode, yearBucketKey);
    if (error) {
      showSaved("Update failed");
      return;
    }
    logAudit("unleashed_product_map_set", {
      item_kind: kind,
      item_key: key,
      year_bucket: yearBucketKey,
      product_code: productCode,
    });
    refresh();
    showSaved("Mapping saved ✓");
  }

  const needsAttention = REQUIRED_PRODUCT_MAP_KEYS.filter(function (k) {
    const code = byKey[`${k.kind}|${k.key}`];
    return !code || code === FIX_IT_CODE;
  }).length;

  const byKind = React.useMemo(function () {
    const g = {};
    for (const k of KIND_ORDER) g[k] = [];
    for (const k of REQUIRED_PRODUCT_MAP_KEYS) {
      if (g[k.kind]) g[k.kind].push(k);
    }
    return g;
  }, []);

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="mb-1 text-[12px] text-zephyr-muted font-semibold">Unleashed</div>
      <div className="flex justify-between items-center mb-3">
        <h1 className="m-0 text-[22px] text-zephyr-text">Product Mapping</h1>
        {savedMsg && <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>}
      </div>
      <p className="m-0 mb-2 text-[13px] text-zephyr-muted">
        Each approved item kind maps to one Unleashed product code, which the Order Builder puts on
        the Sales Order line. Uniforms are mapped on the supplier record, not here.
      </p>
      {needsAttention > 0 && (
        <div className="mb-4 text-[13px] text-[#8a5a00] bg-[#FFF7E6] border border-[#F0C36D] rounded-[6px] py-2 px-3">
          <strong>{needsAttention}</strong> mapping{needsAttention === 1 ? "" : "s"} still need a
          real code (shown as <code>FIX_IT</code> below). Pick the correct product for each.
        </div>
      )}

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading…</div>
      ) : (
        KIND_ORDER.map(function (kind) {
          const keys = byKind[kind] || [];
          return (
            <div key={kind} className="mb-6">
              <h2 className="m-0 mb-2 text-[15px] text-zephyr-teal-dark font-bold">
                {KIND_LABEL[kind]}
              </h2>
              <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
                {keys.map(function (k, idx) {
                  const current = byKey[`${k.kind}|${k.key}`] || "";
                  const attention = !current || current === FIX_IT_CODE;
                  return (
                    <div
                      key={k.key}
                      className="grid items-center gap-3 py-[9px] px-[13px]"
                      style={{
                        gridTemplateColumns: "220px 1fr 150px",
                        borderBottom: idx < keys.length - 1 ? "1px solid #CFE0DE" : "none",
                        background: attention ? "#FFF7E6" : idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                      }}
                    >
                      <label className="text-[13px] text-zephyr-text font-semibold">{k.key}</label>
                      <select
                        aria-label={`Product for ${KIND_LABEL[kind]} / ${k.key}`}
                        value={current}
                        onChange={function (e) {
                          onPick(k.kind, k.key, e.target.value);
                        }}
                        className="py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-full"
                        style={{ fontFamily: "inherit" }}
                      >
                        <option value="">— not mapped —</option>
                        {productGroups.map(function (g) {
                          return (
                            <optgroup key={g.group} label={g.group}>
                              {g.items.map(function (p) {
                                return (
                                  <option key={p.product_code} value={p.product_code}>
                                    {p.product_code}
                                    {p.product_description ? ` — ${p.product_description}` : ""}
                                  </option>
                                );
                              })}
                            </optgroup>
                          );
                        })}
                      </select>
                      <div>
                        {attention ? (
                          <span className="text-[11px] font-bold text-white bg-[#E0A000] rounded-[3px] py-[2px] px-[7px]">
                            NEEDS CODE
                          </span>
                        ) : (
                          <span className="text-[12px] text-[#2E7D32] font-semibold">✓ mapped</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
      {!loading && (
        <div className="mb-6">
          <h2 className="m-0 mb-2 text-[15px] text-zephyr-teal-dark font-bold">
            Stationery packs by school year
          </h2>
          <p className="m-0 mb-2 text-[13px] text-zephyr-muted">
            A QLD Stationery pack uses a year-specific product code (STAP / STA1 / STA2 / STA3-4 /
            STA5-6 / STAHS). The child's year level picks the code automatically. Any year left
            unset falls back to the base <strong>Stationery</strong> mapping in Fees above.
          </p>
          <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
            {STATIONERY_BUCKETS.map(function (b, idx) {
              const current = stationeryByBucket[b.bucket] || "";
              return (
                <div
                  key={b.bucket}
                  className="grid items-center gap-3 py-[9px] px-[13px]"
                  style={{
                    gridTemplateColumns: "220px 1fr 150px",
                    borderBottom:
                      idx < STATIONERY_BUCKETS.length - 1 ? "1px solid #CFE0DE" : "none",
                    background: idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                  }}
                >
                  <label className="text-[13px] text-zephyr-text font-semibold">{b.label}</label>
                  <select
                    aria-label={`Stationery product for ${b.label}`}
                    value={current}
                    onChange={function (e) {
                      onPick("fee", "Stationery", e.target.value, b.bucket);
                    }}
                    className="py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-full"
                    style={{ fontFamily: "inherit" }}
                  >
                    <option value="">— use base Stationery (fallback) —</option>
                    {productGroups.map(function (g) {
                      return (
                        <optgroup key={g.group} label={g.group}>
                          {g.items.map(function (p) {
                            return (
                              <option key={p.product_code} value={p.product_code}>
                                {p.product_code}
                                {p.product_description ? ` — ${p.product_description}` : ""}
                              </option>
                            );
                          })}
                        </optgroup>
                      );
                    })}
                  </select>
                  <div>
                    {current && current !== FIX_IT_CODE ? (
                      <span className="text-[12px] text-[#2E7D32] font-semibold">✓ {current}</span>
                    ) : (
                      <span className="text-[11px] text-zephyr-muted">fallback</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && (
        <div className="mb-6">
          <h2 className="m-0 mb-2 text-[15px] text-zephyr-teal-dark font-bold">Uniforms</h2>
          <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
            <div className="py-[9px] px-[13px] text-[13px] text-zephyr-text">
              All mappings for Uniforms are done on the <strong>Suppliers</strong> screen. Edit the
              supplier <strong>SO Suffix</strong> and <strong>Unleashed Code</strong>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
